import { CarHireSearchFilterKeys } from 'constants/carHire'

const CAR_HIRE_SEARCH_REFINEMENTS_STORAGE_KEY = 'carHireSearchRefinements'
const CAR_HIRE_SEARCH_REFINEMENTS_APPLIED_STORAGE_KEY = 'carHireSearchRefinementsAppliedSearch'
const CAR_HIRE_SEARCH_REFINEMENTS_SKIPPED_STORAGE_KEY = 'carHireSearchRefinementsSkippedSearch'

const CarHireSearchRefinementKeys = Array.from(new Set([
  'sortBy',
  'priceType',
  'price.min',
  'price.max',
  'insurance',
  ...CarHireSearchFilterKeys,
]))

const CarHireSearchCriteriaKeys = [
  'pickUpName',
  'dropOffName',
  'pickUpId',
  'dropOffId',
  'pickUpDate',
  'pickUpTime',
  'dropOffDate',
  'dropOffTime',
  'ageCategory',
  'age',
  'isPickUpAtAirport',
  'pickUpSearchType',
  'dropOffSearchType',
  'bundledOrderId',
  'pickUpLatitude',
  'pickUpLongitude',
  'dropOffLatitude',
  'dropOffLongitude',
]

type StoredCarHireSearchRefinements = Record<string, Array<string>>

function getSessionStorage() {
  if (typeof window === 'undefined') {
    return
  }

  try {
    return window.sessionStorage
  } catch {
    return
  }
}

function getSearchParams(search: string | URLSearchParams) {
  return search instanceof URLSearchParams ? search : new URLSearchParams(search)
}

function normaliseSearch(search: string | URLSearchParams) {
  return getSearchParams(search).toString()
}

function getRefinementsFromSearch(search: string | URLSearchParams): StoredCarHireSearchRefinements {
  const searchParams = getSearchParams(search)

  return CarHireSearchRefinementKeys.reduce<StoredCarHireSearchRefinements>((refinements, key) => {
    const values = searchParams.getAll(key)

    if (values.length > 0) {
      refinements[key] = values
    }

    return refinements
  }, {})
}

export function hasCarHireSearchRefinements(search: string | URLSearchParams) {
  return Object.keys(getRefinementsFromSearch(search)).length > 0
}

export function haveCarHireSearchCriteriaChanged(previousSearch: string, nextSearch: string) {
  const previousSearchParams = new URLSearchParams(previousSearch)
  const nextSearchParams = new URLSearchParams(nextSearch)

  return CarHireSearchCriteriaKeys.some((key) => {
    return previousSearchParams.get(key) !== nextSearchParams.get(key)
  })
}

function getStoredRefinements(): StoredCarHireSearchRefinements {
  const storage = getSessionStorage()
  if (!storage) {
    return {}
  }

  try {
    const storedValue = storage.getItem(CAR_HIRE_SEARCH_REFINEMENTS_STORAGE_KEY)
    if (!storedValue) {
      return {}
    }

    const parsedValue = JSON.parse(storedValue)
    if (!parsedValue || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
      return {}
    }

    return Object.entries(parsedValue).reduce<StoredCarHireSearchRefinements>((refinements, [key, value]) => {
      if (!CarHireSearchRefinementKeys.includes(key) || !Array.isArray(value)) {
        return refinements
      }

      const values = value.filter((item): item is string => typeof item === 'string')
      if (values.length > 0) {
        refinements[key] = values
      }

      return refinements
    }, {})
  } catch {
    return {}
  }
}

export function persistCarHireSearchRefinements(search: string | URLSearchParams) {
  const storage = getSessionStorage()
  if (!storage) {
    return
  }

  try {
    storage.setItem(
      CAR_HIRE_SEARCH_REFINEMENTS_STORAGE_KEY,
      JSON.stringify(getRefinementsFromSearch(search)),
    )
  } catch {
    // Ignore storage write failures so search still works in restricted browsers.
  }
}

export function markCarHireSearchRefinementsApplied(search: string | URLSearchParams) {
  const storage = getSessionStorage()
  if (!storage) {
    return
  }

  try {
    storage.setItem(CAR_HIRE_SEARCH_REFINEMENTS_APPLIED_STORAGE_KEY, normaliseSearch(search))
  } catch {
    // Ignore storage write failures so search still works in restricted browsers.
  }
}

export function markCarHireSearchRefinementsSkipped(search: string | URLSearchParams) {
  const storage = getSessionStorage()
  if (!storage) {
    return
  }

  try {
    storage.setItem(CAR_HIRE_SEARCH_REFINEMENTS_SKIPPED_STORAGE_KEY, normaliseSearch(search))
  } catch {
    // Ignore storage write failures so search still works in restricted browsers.
  }
}

export function consumeCarHireSearchRefinementsApplied(search: string | URLSearchParams) {
  const storage = getSessionStorage()
  if (!storage) {
    return false
  }

  try {
    const appliedSearch = storage.getItem(CAR_HIRE_SEARCH_REFINEMENTS_APPLIED_STORAGE_KEY)
    if (appliedSearch !== normaliseSearch(search)) {
      return false
    }

    storage.removeItem(CAR_HIRE_SEARCH_REFINEMENTS_APPLIED_STORAGE_KEY)
    return true
  } catch {
    return false
  }
}

export function consumeCarHireSearchRefinementsSkipped(search: string | URLSearchParams) {
  const storage = getSessionStorage()
  if (!storage) {
    return false
  }

  try {
    const skippedSearch = storage.getItem(CAR_HIRE_SEARCH_REFINEMENTS_SKIPPED_STORAGE_KEY)
    if (skippedSearch !== normaliseSearch(search)) {
      return false
    }

    storage.removeItem(CAR_HIRE_SEARCH_REFINEMENTS_SKIPPED_STORAGE_KEY)
    return true
  } catch {
    return false
  }
}

export function removeCarHireSearchRefinements(search: string | URLSearchParams) {
  const searchParams = getSearchParams(search)

  CarHireSearchRefinementKeys.forEach((key) => {
    searchParams.delete(key)
  })

  return searchParams.toString()
}

export function applyCarHireSearchRefinements(search: string, currentSearch?: string) {
  const refinements = currentSearch === undefined ? getStoredRefinements() : getRefinementsFromSearch(currentSearch)
  const searchParams = new URLSearchParams(search)

  Object.entries(refinements).forEach(([key, values]) => {
    if (searchParams.has(key)) {
      return
    }

    values.forEach((value) => {
      searchParams.append(key, value)
    })
  })

  return searchParams.toString()
}
